import { TextBlock } from '../interfaces/textEditor.interface';

/**
 * Extract plain text content from TextBlock array for story analysis
 * @param {TextBlock[]} textBlocks - Array of text blocks from your editor
 * @returns {string} - Plain text content suitable for AI analysis
 */
export const extractTextFromBlocks = (textBlocks: TextBlock[]): string => {
  if (!Array.isArray(textBlocks)) {
    return '';
  }

  const extractedText = textBlocks
    .map((block) => extractTextFromBlock(block))
    .filter((text) => text.trim().length > 0) // Remove empty blocks
    .join('\n\n'); // Separate blocks with double newlines

  return extractedText.trim();
};

/**
 * Extract text from a single TextBlock
 * @param {TextBlock} block - Single text block
 * @returns {string} - Extracted text content
 */
const extractTextFromBlock = (block: any) => {
  if (!block || !block.type) {
    return '';
  }

  switch (block.type) {
    case 'Paragraph':
      return extractParagraphText(block);

    case 'Heading':
      return extractHeadingText(block);

    case 'List':
      return extractListText(block);

    case 'Quote':
      return extractQuoteText(block);

    case 'Code':
      return extractCodeText(block);

    case 'Image':
      return extractImageText(block);

    default:
      // Fallback: try to extract any text content
      return extractFallbackText(block);
  }
};

/**
 * Extract text from paragraph blocks
 */
const extractParagraphText = (block: any) => {
  if (block.data?.paragraph) {
    return cleanText(block.data.paragraph);
  }

  // Handle nested blocks structure
  if (block.blocks) {
    return extractTextFromBlocks(block.blocks);
  }

  return '';
};

/**
 * Extract text from heading blocks
 */
const extractHeadingText = (block: any) => {
  if (block.data?.header) {
    return cleanText(block.data.header);
  }

  // Alternative field names
  if (block.data?.text) {
    return cleanText(block.data.text);
  }

  return '';
};

/**
 * Extract text from list blocks
 */
const extractListText = (block: any) => {
  let listText = '';

  // Handle different list formats
  if (block.data?.items && Array.isArray(block.data.items)) {
    listText = block.data.items
      .map((item: any) => {
        if (typeof item === 'string') {
          return `• ${cleanText(item)}`;
        } else if (item.text) {
          return `• ${cleanText(item.text)}`;
        }
        return '';
      })
      .filter((item: string) => item.length > 0)
      .join('\n');
  }

  return listText;
};

/**
 * Extract text from quote blocks
 */
const extractQuoteText = (block: any) => {
  if (block.data?.text) {
    return `"${cleanText(block.data.text)}"`;
  }

  if (block.data?.quote) {
    return `"${cleanText(block.data.quote)}"`;
  }

  return '';
};

/**
 * Extract text from code blocks (include as text for context)
 */
const extractCodeText = (block: any) => {
  if (block.data?.code) {
    return `[Code block: ${cleanText(block.data.code).substring(0, 100)}...]`;
  }

  return '[Code block]';
};

/**
 * Extract text from image blocks (use alt text, captions)
 */
const extractImageText = (block: any) => {
  let imageText = '[Image';

  if (block.data?.caption) {
    imageText += `: ${cleanText(block.data.caption)}`;
  } else if (block.data?.alt) {
    imageText += `: ${cleanText(block.data.alt)}`;
  } else if (block.data?.file?.alt) {
    imageText += `: ${cleanText(block.data.file.alt)}`;
  }

  imageText += ']';
  return imageText;
};

/**
 * Fallback text extraction for unknown block types
 */
const extractFallbackText = (block: any) => {
  // Try common text fields
  const textFields = ['text', 'content', 'paragraph', 'header', 'title'];

  for (const field of textFields) {
    if (block.data?.[field] && typeof block.data[field] === 'string') {
      return cleanText(block.data[field]);
    }
  }

  // If block has nested blocks, recurse
  if (block.blocks) {
    return extractTextFromBlocks(block.blocks);
  }

  return '';
};

/**
 * Clean and normalize extracted text
 */
const cleanText = (text: any) => {
  if (typeof text !== 'string') {
    return '';
  }

  return text
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ') // Replace HTML entities
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
};

/**
 * Get story metadata from TextBlocks (for analytics)
 */
export const getStoryMetadata = (textBlocks: any) => {
  const text = extractTextFromBlocks(textBlocks);
  const blockCounts: Record<string, number> = {};
  // Count different block types
  textBlocks.forEach((block: any) => {
    if (block.type) {
      blockCounts[block.type] = (blockCounts[block.type] || 0) + 1;
    }
  });

  return {
    totalBlocks: textBlocks.length,
    blockTypes: blockCounts,
    wordCount: text.split(/\s+/).filter((word) => word.length > 0).length,
    characterCount: text.length,
    estimatedReadingTime: Math.ceil(text.split(/\s+/).length / 200), // ~200 WPM
    hasImages: blockCounts['Image'] > 0,
    hasCode: blockCounts['Code'] > 0,
    hasLists: blockCounts['List'] > 0,
  };
};

// Example usage and testing
// if (typeof module !== 'undefined' && module.exports) {
//   // Node.js environment - for testing
//
//   const testTextBlocks = [
//     {
//       id: "header1",
//       type: "Heading",
//       data: { header: "My Amazing Story" }
//     },
//     {
//       id: "para1",
//       type: "Paragraph",
//       data: { paragraph: "Today was an incredible day that changed my perspective on life." }
//     },
//     {
//       id: "para2",
//       type: "Paragraph",
//       data: { paragraph: "I volunteered at a local shelter and met inspiring people." }
//     },
//     {
//       id: "img1",
//       type: "Image",
//       data: {
//         file: { url: "image.jpg" },
//         caption: "People volunteering at the shelter"
//       }
//     },
//     {
//       id: "list1",
//       type: "List",
//       data: {
//         items: [
//           "Met amazing volunteers",
//           "Learned about community service",
//           "Gained new perspective"
//         ]
//       }
//     }
//   ];
//
//
//   module.exports = {
//     extractTextFromBlocks,
//     getStoryMetadata
//   };
// }
